import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Button, Spinner, Form } from 'react-bootstrap';
import { authService } from '../services/api';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [message, setMessage] = useState('');
  const [validated, setValidated] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    // Validation du formulaire
    if (!form.checkValidity() || password !== confirmPassword) {
      e.stopPropagation();
      setValidated(true);
      if (password !== confirmPassword) {
        setStatus('error');
        setMessage('Les mots de passe ne correspondent pas.');
      }
      return;
    }

    try {
      setStatus('submitting');
      const response = await authService.resetPassword(token, password);
      setStatus('success');
      setMessage(response.msg || 'Votre mot de passe a été réinitialisé avec succès.');
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      setMessage(error.msg || 'Erreur lors de la réinitialisation du mot de passe. Le token est peut-être invalide ou expiré.');
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Réinitialisation du mot de passe</h4>
            </Card.Header>
            <Card.Body>
              {!token ? (
                <Alert variant="danger">
                  <Alert.Heading>Erreur</Alert.Heading>
                  <p>Token de réinitialisation manquant. Veuillez utiliser le lien complet envoyé dans l'email de réinitialisation.</p>
                  <hr />
                  <p className="mb-0">
                    <Link to="/forgot-password">Demander un nouveau lien de réinitialisation</Link>
                  </p>
                </Alert>
              ) : (
                <>
                  {status === 'success' ? (
                    <Alert variant="success">
                      <Alert.Heading>Réinitialisation réussie!</Alert.Heading>
                      <p>{message}</p>
                      <p>Vous allez être redirigé vers la page de connexion dans quelques secondes...</p>
                      <hr />
                      <div className="d-flex justify-content-end">
                        <Button variant="outline-success" as={Link} to="/login">
                          Aller à la page de connexion
                        </Button>
                      </div>
                    </Alert>
                  ) : (
                    <>
                      {status === 'error' && (
                        <Alert variant="danger" className="mb-4">
                          {message}
                        </Alert>
                      )}
                      
                      <p className="mb-4">Veuillez entrer votre nouveau mot de passe ci-dessous :</p>
                      
                      <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="password">
                          <Form.Label>Nouveau mot de passe</Form.Label>
                          <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            placeholder="Entrez votre nouveau mot de passe"
                          />
                          <Form.Control.Feedback type="invalid">
                            Le mot de passe doit contenir au moins 6 caractères.
                          </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group className="mb-4" controlId="confirmPassword">
                          <Form.Label>Confirmer le mot de passe</Form.Label>
                          <Form.Control
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            isInvalid={validated && password !== confirmPassword}
                            placeholder="Confirmez votre nouveau mot de passe"
                          />
                          <Form.Control.Feedback type="invalid">
                            Les mots de passe ne correspondent pas.
                          </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Button 
                          type="submit" 
                          variant="primary" 
                          disabled={status === 'submitting'}
                          className="w-100"
                        >
                          {status === 'submitting' ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                              <span className="ms-2">Traitement en cours...</span>
                            </>
                          ) : 'Réinitialiser le mot de passe'}
                        </Button>
                      </Form>
                    </>
                  )}
                </>
              )}
            </Card.Body>
            <Card.Footer className="text-center">
              <div className="d-flex justify-content-between">
                <Button variant="link" as={Link} to="/login" className="text-decoration-none">
                  Connexion
                </Button>
                <Button variant="link" as={Link} to="/register" className="text-decoration-none">
                  Inscription
                </Button>
                <Button variant="link" as={Link} to="/" className="text-decoration-none">
                  Accueil
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
